import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { appointmentAPI, orderAPI, authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatPrice, formatDate, formatShortDate, getStatusLabel, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import {
  Scissors,
  Calendar,
  ShoppingBag,
  User,
  LogOut,
  Clock,
  MapPin,
  Edit,
  Key,
  X,
  CheckCircle
} from 'lucide-react';

export default function UserDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Profile edit states
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    phone: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password change states
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [appointmentsRes, ordersRes] = await Promise.all([
        appointmentAPI.list({}).catch(() => ({ data: [] })),
        orderAPI.list().catch(() => ({ data: [] }))
      ]);
      setAppointments(appointmentsRes.data || []);
      setOrders(ordersRes.data || []);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logout effettuato');
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm('Sei sicuro di voler annullare questo appuntamento?')) return;
    try {
      await appointmentAPI.cancel(appointmentId);
      toast.success('Appuntamento annullato');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore annullamento appuntamento');
    }
  };

  const handleUpdateProfile = async () => {
    if (!profileData.name.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    
    try {
      setSavingProfile(true);
      await authAPI.updateProfile(profileData);
      toast.success('Profilo aggiornato');
      setEditProfileOpen(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento profilo');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('La nuova password deve essere di almeno 6 caratteri');
      return;
    }
    
    try {
      setSavingPassword(true);
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password cambiata con successo');
      setChangePasswordOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore cambio password');
    } finally {
      setSavingPassword(false);
    }
  };

  const upcomingAppointments = appointments.filter(
    apt => apt.status !== 'ANNULLATO' && apt.status !== 'COMPLETATO'
  );

  const canCancelAppointment = (apt) => {
    if (apt.status === 'ANNULLATO' || apt.status === 'COMPLETATO') return false;
    // Allow cancellation only if appointment is in the future
    const appointmentDate = new Date(`${apt.date}T${apt.time}`);
    return appointmentDate > new Date();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="container-main flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <Scissors className="w-6 h-6 text-primary" />
            <span className="font-semibold">AppLook</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">
              Ciao, {user?.name}
            </span>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-12 section-padding">
        <div className="container-main">
          {/* Welcome */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">La Tua Dashboard</h1>
            <p className="text-muted-foreground">
              Gestisci i tuoi appuntamenti e ordini
            </p>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{upcomingAppointments.length}</p>
                    <p className="text-sm text-muted-foreground">Appuntamenti</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <ShoppingBag className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{orders.length}</p>
                    <p className="text-sm text-muted-foreground">Ordini</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {appointments.filter(a => a.status === 'COMPLETATO').length}
                    </p>
                    <p className="text-sm text-muted-foreground">Completati</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Content Tabs */}
          <Tabs defaultValue="appointments">
            <TabsList>
              <TabsTrigger value="appointments" data-testid="tab-appointments">
                <Calendar className="w-4 h-4 mr-2" />
                Appuntamenti
              </TabsTrigger>
              <TabsTrigger value="orders" data-testid="tab-orders">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Ordini
              </TabsTrigger>
              <TabsTrigger value="profile" data-testid="tab-profile">
                <User className="w-4 h-4 mr-2" />
                Profilo
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="appointments" className="mt-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : appointments.length === 0 ? (
                <Card className="p-8 text-center">
                  <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Nessun appuntamento</h3>
                  <p className="text-muted-foreground mb-4">
                    Non hai ancora prenotato appuntamenti.
                  </p>
                  <Button asChild>
                    <Link to="/">Prenota Ora</Link>
                  </Button>
                </Card>
              ) : (
                <div className="space-y-4">
                  {appointments.map((apt) => (
                    <Card key={apt.id} data-testid={`appointment-${apt.id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold">{apt.service?.name || 'Servizio'}</h3>
                              <Badge className={`status-badge ${getStatusColor(apt.status)}`}>
                                {getStatusLabel(apt.status)}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatShortDate(apt.date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {apt.time}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {apt.staff?.user?.name || 'Operatore'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <p className="font-bold text-primary">
                              {apt.service?.price ? formatPrice(apt.service.price) : '-'}
                            </p>
                            {canCancelAppointment(apt) && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleCancelAppointment(apt.id)}
                                data-testid={`cancel-appointment-${apt.id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Annulla
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="orders" className="mt-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : orders.length === 0 ? (
                <Card className="p-8 text-center">
                  <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Nessun ordine</h3>
                  <p className="text-muted-foreground mb-4">
                    Non hai ancora effettuato ordini.
                  </p>
                  <Button asChild>
                    <Link to="/">Vai allo Shop</Link>
                  </Button>
                </Card>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <Card key={order.id} data-testid={`order-${order.id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold">Ordine #{order.id.slice(0, 8)}</h3>
                              <Badge className={`status-badge ${getStatusColor(order.payment_status)}`}>
                                {getStatusLabel(order.payment_status)}
                              </Badge>
                              {order.status && order.status !== 'pending' && (
                                <Badge variant="outline">{order.status}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {order.items?.length || 0} prodotti
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-primary">{formatPrice(order.total)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="profile" className="mt-6">
              <div className="grid gap-6">
                {/* Profile Info Card */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Il Tuo Profilo</CardTitle>
                      <CardDescription>Gestisci le tue informazioni personali</CardDescription>
                    </div>
                    <Dialog open={editProfileOpen} onOpenChange={setEditProfileOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" data-testid="edit-profile-btn">
                          <Edit className="w-4 h-4 mr-2" />
                          Modifica
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Modifica Profilo</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label>Nome</Label>
                            <Input 
                              value={profileData.name}
                              onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                              data-testid="profile-name-input"
                            />
                          </div>
                          <div>
                            <Label>Telefono</Label>
                            <Input 
                              value={profileData.phone}
                              onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                              data-testid="profile-phone-input"
                            />
                          </div>
                          <div>
                            <Label>Email</Label>
                            <Input 
                              value={user?.email || ''}
                              disabled
                              className="bg-muted"
                            />
                            <p className="text-xs text-muted-foreground mt-1">L'email non può essere modificata</p>
                          </div>
                          <Button 
                            onClick={handleUpdateProfile} 
                            className="w-full" 
                            disabled={savingProfile}
                            data-testid="save-profile-btn"
                          >
                            {savingProfile ? 'Salvataggio...' : 'Salva Modifiche'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-muted-foreground">Nome</label>
                        <p className="font-medium">{user?.name}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Email</label>
                        <p className="font-medium">{user?.email}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Telefono</label>
                        <p className="font-medium">{user?.phone || 'Non specificato'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Ruolo</label>
                        <p className="font-medium">{user?.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Security Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="w-5 h-5" />
                      Sicurezza
                    </CardTitle>
                    <CardDescription>Gestisci la tua password</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" data-testid="change-password-btn">
                          Cambia Password
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Cambia Password</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label>Password Attuale</Label>
                            <Input 
                              type="password"
                              value={passwordData.current_password}
                              onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                              data-testid="current-password-input"
                            />
                          </div>
                          <div>
                            <Label>Nuova Password</Label>
                            <Input 
                              type="password"
                              value={passwordData.new_password}
                              onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                              data-testid="new-password-input"
                            />
                          </div>
                          <div>
                            <Label>Conferma Nuova Password</Label>
                            <Input 
                              type="password"
                              value={passwordData.confirm_password}
                              onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                              data-testid="confirm-password-input"
                            />
                          </div>
                          <Button 
                            onClick={handleChangePassword} 
                            className="w-full"
                            disabled={savingPassword}
                            data-testid="save-password-btn"
                          >
                            {savingPassword ? 'Salvataggio...' : 'Cambia Password'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
